import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { StreamingText } from "./StreamingText";
// Check if text contains markdown patterns
const hasMarkdown = (text) => {
    const markdownPatterns = [
        /^#{1,6}\s/m, // Headers
        /\*\*.*?\*\*/, // Bold
        /\*.*?\*/, // Italic
        /\[.*?\]\(.*?\)/, // Links
        /```[\s\S]*?```/, // Code blocks
        /`.*?`/, // Inline code
        /^\s*[-*+]\s/m, // Unordered lists
        /^\s*\d+\.\s/m, // Ordered lists
        /^>\s/m, // Blockquotes
    ];
    return markdownPatterns.some(pattern => pattern.test(text));
};
export const MessageContent = ({ text, mentions, isAssistant = false, isStreaming = false }) => {
    // If this is an assistant message and contains markdown, render it
    if (isAssistant && hasMarkdown(text) && (!mentions || mentions.length === 0)) {
        return (_jsx("div", { className: "markdown-content leading-relaxed", children: _jsx(StreamingText, { text: text, isStreaming: isStreaming, children: (displayedText) => (_jsx(_Fragment, { children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: {
                            h1: ({ children }) => _jsx("h1", { className: "text-base font-bold mt-4 mb-2 text-gray-900", children: children }),
                            h2: ({ children }) => _jsx("h2", { className: "text-base font-bold mt-3 mb-2 text-gray-900", children: children }),
                            h3: ({ children }) => _jsx("h3", { className: "text-base font-bold mt-3 mb-2 text-gray-900", children: children }),
                            p: ({ children }) => _jsx("p", { className: "mb-3 leading-relaxed text-base", children: children }),
                            ul: ({ children }) => _jsx("ul", { className: "list-disc list-inside mb-3 space-y-1 ml-4", children: children }),
                            ol: ({ children }) => _jsx("ol", { className: "list-decimal list-inside mb-3 space-y-1 ml-4", children: children }),
                            li: ({ children }) => _jsx("li", { className: "leading-relaxed", children: children }),
                            strong: ({ children }) => _jsx("strong", { className: "font-bold text-gray-900", children: children }),
                            em: ({ children }) => _jsx("em", { className: "italic", children: children }),
                            code: ({ children, className }) => {
                                const isInline = !className;
                                return isInline ? (_jsx("code", { className: "bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800", children: children })) : (_jsx("code", { className: className, children: children }));
                            },
                            pre: ({ children }) => (_jsx("pre", { className: "bg-gray-100 p-3 rounded-lg overflow-x-auto mb-3 text-sm font-mono", children: children })),
                            blockquote: ({ children }) => (_jsx("blockquote", { className: "border-l-4 border-gray-300 pl-4 italic my-3 text-gray-700", children: children })),
                            hr: () => _jsx("hr", { className: "my-4 border-gray-300" }),
                            a: ({ href, children }) => (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer", className: "text-blue-600 hover:underline", children: children })),
                        }, children: displayedText }) })) }) }));
    }
    // If there are no mentions, just return plain text
    if (!mentions || mentions.length === 0) {
        return _jsx("span", { className: "leading-relaxed text-(--font-size-base)", children: text });
    }
    // Build the content with mentions as styled elements
    const parts = [];
    let lastIndex = 0;
    // Sort mentions by start index to process them in order
    const sortedMentions = [...mentions].sort((a, b) => a.startIndex - b.startIndex);
    sortedMentions.forEach((mention, index) => {
        // Add text before the mention
        if (mention.startIndex > lastIndex) {
            parts.push(_jsx("span", { children: text.substring(lastIndex, mention.startIndex) }, `text-${index}`));
        }
        // Add the styled mention (gray box style)
        // eslint-disable-next-line
        parts.push(_jsxs("span", { className: "inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-lg", style: {
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                color: '#1f2937',
                fontFamily: 'Mulish, sans-serif',
                fontSize: '15px',
                fontWeight: 400,
                lineHeight: 1.5,
            }, children: [_jsx("span", { children: "@" }), _jsx("span", { children: mention.label })] }, `mention-${mention.id}-${index}`));
        lastIndex = mention.endIndex;
    });
    // Add any remaining text after the last mention
    if (lastIndex < text.length) {
        parts.push(_jsx("span", { children: text.substring(lastIndex) }, "text-end"));
    }
    return _jsx("span", { className: "leading-relaxed inline", children: parts });
};
